"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { clearTokens } from "@/lib/auth";
import { ROUTES } from "@/lib/routes";
import { useAuth } from "@/context/AuthContext";
import { useHasActiveCoupon } from "@/lib/use-coupon";
import { supabase } from "@/lib/supabase";
import { X, LogOut } from "lucide-react";
import Image from "next/image";

const navFont = { fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif" };

const NAV_LINKS = [
  { label: "Pricing", href: ROUTES.pricing },
  { label: "Features", href: ROUTES.features },
  { label: "FAQs", href: ROUTES.faq },
  { label: "API", href: ROUTES.api },
  // Affiliate will be added back to the navbar when the program is ready.
  // { label: "Affiliate", href: ROUTES.affiliateProgram },
];

const navLinkClassName = (isActive: boolean, isMobile = false) =>
  [
    isMobile
      ? "text-[15px] font-medium transition-colors rounded-lg px-3 py-2.5 cursor-pointer"
      : "text-[14px] font-medium transition-colors cursor-pointer",
    isActive
      ? "text-[#1464FF]"
      : isMobile
        ? "text-[#4A4D60] hover:text-[#1B1C30] hover:bg-[#F5F6FA]"
        : "text-[#4A4D60] hover:text-[#1B1C30]",
  ].join(" ");

export default function Navbar() {
  const { isAuthenticated } = useAuth();
  const hasCoupon = useHasActiveCoupon();
  const pathname = usePathname();

  // Coupon-aware redirect: when a recognized coupon trigger is active, the
  // Login button points at the pricing surface so the visitor can finish
  // checkout. EXCEPT when we're already on the pricing surface — there
  // ROUTES.pricing would self-loop and the button looks broken to anyone who
  // has a stale `landing_page_coupon` in localStorage from a months-old
  // visit (LIS-511 follow-up; red-teamed during PR #392 review).
  const loginHref =
    hasCoupon && pathname !== ROUTES.pricing ? ROUTES.pricing : ROUTES.login;

  // Keep html.is-authed in sync with React auth state as a fallback/backup
  // to the inline pre-hydration script (handles SSR, bfcache, edge cases).
  useEffect(() => {
    if (isAuthenticated) {
      document.documentElement.classList.add('is-authed');
    } else {
      document.documentElement.classList.remove('is-authed');
    }
  }, [isAuthenticated]);

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);

    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    // Sign out directly here so the user doesn't need to round-trip through
    // the React app's /logout route. Default (global) scope, matching the
    // app's own signOut() (apps/frontend/src/context/AuthContext.tsx) —
    // invalidates the refresh token server-side so the session can't be
    // silently restored elsewhere. Also clear Supabase's own storage via
    // the SDK, plus any stray `sb-*-auth-token` keys if the SDK's cached
    // client fails to find the session (see AuthContext comment re: stale
    // singleton).
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore — fall through to manual cleanup
    }
    try {
      Object.keys(localStorage)
        .filter((k) => /^sb-.*-auth-token$/.test(k))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      // localStorage unavailable — nothing we can do
    }
    clearTokens();
    // Full reload so the pre-hydration script re-reads (empty) auth state
    // and the navbar repaints as logged-out even on cache-served renders.
    window.location.assign("/");
  };

  const isActiveLink = (href: string) => !href.includes("#") && pathname === href;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled
          ? "backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
          : ""
      }`}
      style={{ width: "100%", height: 64 }}
    >
      <nav className="flex items-center justify-between h-[64px] w-full px-6 lg:px-12">
        <Link href="/">
          <Image src="logo/logo-full.png" alt="Speedy Listing" width="168" height="40" className="object-contain" unoptimized />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center justify-end gap-8">
          {NAV_LINKS.map((link) => {
            const isActive = isActiveLink(link.href);

            return (
              <a
                key={link.label}
                href={link.href}
                aria-current={isActive ? "page" : undefined}
                className={navLinkClassName(isActive)}
                style={navFont}
              >
                {link.label}
              </a>
            );
          })}

          {/* Render BOTH states; pre-hydration inline script in layout sets
              html.is-authed to flip visibility via CSS, so the correct links
              show even before React hydrates (covers bfcache/cache restores). */}
          <div className="auth-out contents">
            <a
                href={loginHref}
                className="text-[13px] font-semibold text-[#1464FF] cursor-pointer transition-all rounded-full px-5 py-2.5"
                style={navFont}
              >
                Login
              </a>
            <a
              href={ROUTES.signup}
              className="text-[13px] font-semibold text-[#1464FF] bg-[#1464FF]/12 hover:bg-[#1464FF] hover:text-white transition-all rounded-full px-5 py-2.5 cursor-pointer"
              style={navFont}
            >
              Get Started Free
            </a>
          </div>
          <div className="auth-in contents">
            <a
              href={ROUTES.campaigns}
              className="text-[13px] font-semibold text-white bg-[#1464FF] hover:bg-[#0D4FD6] transition-all rounded-full px-5 py-2.5 cursor-pointer"
              style={navFont}
            >
              Dashboard
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-[13px] font-semibold text-[#4A4D60] hover:bg-gray-100 transition-all rounded-full px-4 py-2.5 cursor-pointer"
              style={navFont}
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
          {/* Hidden marker so we can still read React-driven isAuthenticated
              in devtools if needed */}
          <span data-auth={isAuthenticated ? "in" : "out"} className="hidden" />
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {mobileMenuOpen ? (
            <X className="w-[22px] h-[22px] text-[#1B1C30]" />
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1B1C30" strokeWidth="1.5">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div ref={menuRef} className="md:hidden bg-white border-t border-[#E4E7EF] shadow-lg animate-slide-down">
          <div className="flex flex-col px-6 py-4 gap-1">
            {NAV_LINKS.map((link) => {
              const isActive = isActiveLink(link.href);

              return (
                <a
                  key={link.label}
                  href={link.href}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => setMobileMenuOpen(false)}
                  className={navLinkClassName(isActive, true)}
                  style={navFont}
                >
                  {link.label}
                </a>
              );
            })}

            <div className="border-t border-[#E4E7EF] my-2" />

            <div className="auth-out contents">
              <a
                href={loginHref}
                onClick={() => setMobileMenuOpen(false)}
                className="text-[15px] font-semibold text-[#1464FF] hover:bg-[#1464FF]/8 transition-all rounded-lg px-3 py-2.5 cursor-pointer"
                style={navFont}
              >
                Login
              </a>
              <a
                href={ROUTES.signup}
                onClick={() => setMobileMenuOpen(false)}
                className="text-[15px] font-semibold text-[#1464FF] hover:bg-[#1464FF]/12 transition-all rounded-full px-3 py-2.5 text-left cursor-pointer"
                style={navFont}
              >
                Get Started Free
              </a>
            </div>
            <div className="auth-in contents">
              <a
                href={ROUTES.campaigns}
                className="text-[15px] font-semibold text-[#1464FF] hover:bg-[#1464FF]/8 transition-all rounded-lg px-3 py-2.5 cursor-pointer"
                style={navFont}
              >
                Dashboard
              </a>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-left text-[15px] font-semibold text-[#4A4D60] hover:bg-gray-100 transition-all rounded-lg px-3 py-2.5 cursor-pointer"
                style={navFont}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
